import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Home as HomeIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  AspectRatio as SizeIcon,
  CalendarToday as YearIcon,
  AttachMoney as PriceIcon,
  LocationOn as LocationIcon,
  Person as AgentIcon,
  School as SchoolIcon,
  Camera as PhotoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { PropertyParserResult } from '../../../types/property';

interface PropertyDataTabProps {
  data: PropertyParserResult;
  loading?: boolean;
  error?: string;
}

const PropertyDataTab: React.FC<PropertyDataTabProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading property data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No property data available
      </Alert>
    );
  }

  const { property, verification } = data;

  const getVerificationColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatSquareFootage = (sqft: number) => {
    return `${sqft.toLocaleString()} sq ft`;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Verification Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Data Verification</Typography>
            <Chip
              icon={verification.confidence >= 0.8 ? <CheckIcon /> : verification.confidence >= 0.6 ? <WarningIcon /> : <ErrorIcon />}
              label={`${Math.round(verification.confidence * 100)}% Confidence`}
              color={getVerificationColor(verification.confidence)}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Data Quality: <strong>{verification.dataQuality}</strong>
          </Typography>
          
          {verification.missingFields.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Missing fields: {verification.missingFields.join(', ')}
            </Alert>
          )}
          
          {verification.suggestions.length > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Suggestions: {verification.suggestions.join('; ')}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Basic Property Information */}
        <Box sx={{ flex: { xs: 1, md: 2 } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon color="primary" />
                Property Overview
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                  {property.address}
                </Typography>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {formatPrice(property.price)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {property.pricePerSqFt ? `$${property.pricePerSqFt}/sq ft` : ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px', textAlign: 'center', p: 1 }}>
                  <BedIcon color="primary" sx={{ mb: 1 }} />
                  <Typography variant="h6">{property.bedrooms}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Bedrooms
                  </Typography>
                </Box>
                
                <Box sx={{ flex: '1 1 200px', textAlign: 'center', p: 1 }}>
                  <BathtubIcon color="primary" sx={{ mb: 1 }} />
                  <Typography variant="h6">{property.bathrooms}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Bathrooms
                  </Typography>
                </Box>
                
                <Box sx={{ flex: '1 1 200px', textAlign: 'center', p: 1 }}>
                  <SizeIcon color="primary" sx={{ mb: 1 }} />
                  <Typography variant="h6">
                    {property.squareFootage ? formatSquareFootage(property.squareFootage) : 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Square Feet
                  </Typography>
                </Box>
                
                <Box sx={{ flex: '1 1 200px', textAlign: 'center', p: 1 }}>
                  <YearIcon color="primary" sx={{ mb: 1 }} />
                  <Typography variant="h6">{property.yearBuilt}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Year Built
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Additional Details */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Property Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 250px' }}>
                  <Typography variant="body2" color="text.secondary">Property Type</Typography>
                  <Typography variant="body1">{property.propertyType}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 250px' }}>
                  <Typography variant="body2" color="text.secondary">Listing Status</Typography>
                  <Chip 
                    label={property.listingStatus} 
                    color={property.listingStatus === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: '1 1 250px' }}>
                  <Typography variant="body2" color="text.secondary">Condition</Typography>
                  <Typography variant="body1">{property.condition || 'N/A'}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 250px' }}>
                  <Typography variant="body2" color="text.secondary">Lot Size</Typography>
                  <Typography variant="body1">
                    {property.lotSize ? `${property.lotSize} acres` : 'N/A'}
                  </Typography>
                </Box>
                {property.mlsNumber && (
                  <Box sx={{ flex: '1 1 250px' }}>
                    <Typography variant="body2" color="text.secondary">MLS Number</Typography>
                    <Typography variant="body1">{property.mlsNumber}</Typography>
                  </Box>
                )}
              </Box>

              {/* Property Description */}
              {property.description && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {property.description}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Agent & Additional Info */}
        <Box sx={{ flex: { xs: 1, md: 1 }, maxWidth: { md: 400 } }}>
          {/* Listing Agent */}
          {property.agent && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AgentIcon color="primary" />
                  Listing Agent
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar>
                    <AgentIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {property.agent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {property.agent.phone}
                    </Typography>
                    {property.agent.dreLicense && (
                      <Typography variant="caption" color="text.secondary">
                        DRE: {property.agent.dreLicense}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* HOA Information */}
          {property.hoa && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  HOA Information
                </Typography>
                
                <Typography variant="body1" fontWeight="bold">
                  ${property.hoa.fee}/{property.hoa.frequency}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {property.hoa.name}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Photos */}
          {property.photos && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoIcon color="primary" />
                  Photos
                </Typography>
                
                <Typography variant="h4" color="primary">
                  {property.photos.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available photos
                </Typography>
                
                {property.photos.types && property.photos.types.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {property.photos.types.map((type, index) => (
                      <Chip key={index} label={type} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Schools */}
          {property.schools && property.schools.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon color="primary" />
                  Nearby Schools
                </Typography>
                
                <List dense>
                  {property.schools.map((school, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <SchoolIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={school}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Features */}
      <Box sx={{ mt: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Property Features
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                {property.features.interior && property.features.interior.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Interior Features
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {property.features.interior.map((feature, index) => (
                        <Chip key={index} label={feature} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {property.features.exterior && property.features.exterior.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Exterior Features
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {property.features.exterior.map((feature, index) => (
                        <Chip key={index} label={feature} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {property.features.appliances && property.features.appliances.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Appliances
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {property.features.appliances.map((appliance, index) => (
                        <Chip key={index} label={appliance} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
      </Box>
    </Box>
  );
};

export default PropertyDataTab;